import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  MapPin,
  Building2,
  BarChart3,
  Shield,
  Loader2,
  Sparkles,
  Navigation,
  ArrowRight,
} from 'lucide-react';
import { useJsApiLoader } from '@react-google-maps/api';
import FullScreenAnalysis from '../components/Chatbot/FullScreenAnalysis';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
const LIBRARIES = ['places', 'visualization'];

const ChatPage = () => {
  const [address, setAddress] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [recentSearches] = useState([
    '4641 Austell Powder Springs Rd, Austell, GA',
    '1724 Atlanta Hwy, Gainesville, GA',
    '526 Flint Rd, Jonesboro, GA',
  ]);

  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const sessionTokenRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    id: 'lotintel-maps',
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      autocompleteServiceRef.current =
        new window.google.maps.places.AutocompleteService();
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }
  }, [isLoaded]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target) &&
        inputRef.current &&
        !inputRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(
    (input) => {
      if (!autocompleteServiceRef.current || !input.trim()) {
        setSuggestions([]);
        return;
      }
      autocompleteServiceRef.current.getPlacePredictions(
        {
          input,
          componentRestrictions: { country: 'us' },
          types: ['address'],
          sessionToken: sessionTokenRef.current,
        },
        (predictions, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(predictions);
            setShowSuggestions(true);
          } else {
            setSuggestions([]);
          }
        }
      );
    },
    []
  );

  const handleInputChange = (e) => {
    const value = e.target.value;
    setAddress(value);
    if (value.length > 2) {
      fetchSuggestions(value);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handleSelectSuggestion = (description) => {
    setAddress(description);
    setSuggestions([]);
    setShowSuggestions(false);
    // Reset session token after selection
    if (window.google) {
      sessionTokenRef.current =
        new window.google.maps.places.AutocompleteSessionToken();
    }
  };

  const handleAnalyze = () => {
    if (!address.trim()) return;
    setIsAnalyzing(true);
    // Brief animation before opening
    setTimeout(() => {
      setShowFullScreen(true);
      setIsAnalyzing(false);
    }, 600);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      setShowSuggestions(false);
      handleAnalyze();
    }
  };

  const features = [
    {
      icon: Building2,
      title: 'Property Details',
      desc: 'Owner, zoning, lot size & financials',
      gradient: 'from-blue-500 to-cyan-500',
    },
    {
      icon: BarChart3,
      title: 'Competitor Analysis',
      desc: 'Nearby competitors, ratings & amenities',
      gradient: 'from-violet-500 to-purple-500',
    },
    {
      icon: Navigation,
      title: 'Traffic & Volume',
      desc: 'Traffic counts, projections & insights',
      gradient: 'from-amber-500 to-orange-500',
    },
    {
      icon: Shield,
      title: 'Safety & Zoning',
      desc: 'Churches, schools & alcohol zone alerts',
      gradient: 'from-emerald-500 to-green-500',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-5xl mx-auto px-4 py-10">

        {/* Hero Section */}
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-5">
            <Sparkles className="h-4 w-4" />
            Property Intelligence
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            <span className="bg-gradient-to-r from-primary via-primary/80 to-primary/60 bg-clip-text text-transparent">
              Analyze Any Property
            </span>
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Enter a commercial property address to get comprehensive analysis — 
            competitor data, traffic insights, zoning details, and investment projections.
          </p>
        </motion.div>

        {/* Search Card */}
        <motion.div
          className="relative max-w-3xl mx-auto mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div
            className={`
              rounded-2xl border-2 bg-card shadow-lg transition-all duration-300
              ${isFocused ? 'border-primary shadow-primary/20 shadow-xl' : 'border-border'}
            `}
          >
            <div className="p-6">
              <div className="flex items-center gap-3">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 shrink-0">
                  <MapPin className="h-6 w-6 text-primary" />
                </div>
                <div className="flex-1 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={address}
                    onChange={handleInputChange}
                    onKeyDown={handleKeyDown}
                    onFocus={() => {
                      setIsFocused(true);
                      if (suggestions.length > 0) setShowSuggestions(true);
                    }}
                    onBlur={() => setIsFocused(false)}
                    placeholder="Enter a property address to analyze..."
                    className="w-full text-lg bg-transparent border-none outline-none placeholder:text-muted-foreground/50 text-foreground"
                    autoComplete="off"
                  />

                  {/* Autocomplete Suggestions Dropdown */}
                  <AnimatePresence>
                    {showSuggestions && suggestions.length > 0 && (
                      <motion.div
                        ref={suggestionsRef}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute left-0 right-0 top-full mt-3 bg-popover border border-border rounded-xl shadow-xl z-50 overflow-hidden"
                      >
                        {suggestions.map((s) => (
                          <button
                            key={s.place_id}
                            onMouseDown={() => handleSelectSuggestion(s.description)}
                            className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-accent transition-colors group"
                          >
                            <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 group-hover:text-primary shrink-0" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium truncate text-foreground">
                                {s.structured_formatting.main_text}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">
                                {s.structured_formatting.secondary_text}
                              </div>
                            </div>
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <button
                  onClick={handleAnalyze}
                  disabled={!address.trim() || isAnalyzing}
                  className={`
                    flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm
                    transition-all duration-300 shrink-0
                    ${address.trim()
                      ? 'bg-primary text-primary-foreground hover:opacity-90 hover:scale-[1.02] active:scale-[0.98] shadow-md'
                      : 'bg-muted text-muted-foreground cursor-not-allowed'
                    }
                  `}
                >
                  {isAnalyzing ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Search className="h-5 w-5" />
                  )}
                  {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                </button>
              </div>
            </div>

            {/* Quick suggestions */}
            <div className="border-t border-border/50 px-6 py-3 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground font-medium">Recent:</span>
              {recentSearches.map((search, i) => (
                <button
                  key={i}
                  onClick={() => {
                    setAddress(search);
                    setSuggestions([]);
                    setShowSuggestions(false);
                  }}
                  className="text-xs px-3 py-1 rounded-full bg-accent hover:bg-accent/80 text-foreground transition-colors truncate max-w-[250px]"
                >
                  {search}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Feature Cards */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5 + i * 0.1 }}
                className="group rounded-xl border bg-card p-5 hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
              >
                <div
                  className={`w-10 h-10 rounded-lg bg-gradient-to-br ${f.gradient} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300`}
                >
                  <Icon className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-semibold text-sm mb-1">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  {f.desc}
                </p>
              </motion.div>
            );
          })}
        </motion.div>

        {/* How it works */}
        <motion.div
          className="mt-14 max-w-3xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h2 className="text-center text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-6">
            How it works
          </h2>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            {[
              { step: '1', label: 'Enter address' },
              { step: '2', label: 'AI analyzes data' },
              { step: '3', label: 'Get full report' },
            ].map((s, i) => (
              <React.Fragment key={s.step}>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">
                    {s.step}
                  </div>
                  <span className="text-sm font-medium">{s.label}</span>
                </div>
                {i < 2 && (
                  <ArrowRight className="h-4 w-4 text-muted-foreground hidden sm:block" />
                )}
              </React.Fragment>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Full Screen Analysis Modal */}
      {showFullScreen && (
        <FullScreenAnalysis
          address={address}
          onClose={() => setShowFullScreen(false)}
        />
      )}
    </div>
  );
};

export default ChatPage;